<template>
  <div class="ibox">
    <a class="ibox-results-collapse visible-xs" role="button" data-toggle="collapse" href=".ibox-results-filters-collapse-box" aria-expanded="false" aria-controls="ibox-results-filters-collapse-box">
      Show Filters <i class="fa fa-bars"></i>
    </a>
    <div class="ibox-results-filters-collapse-box collapse in">
      <div class="ibox-results-filters">
        <div class="row">
          <div class="col-sm-6 col-lg-4">
            <div class="form-group">
              <label class="control-label">Keywords</label>
              <input type="text" class="form-control" placeholder="Search here.." @input="updateKeywords" />
            </div>
          </div>
          <div class="col-sm-6 col-lg-4">
            <div class="form-group">
              <label class="control-label">Order</label>
              <input type="text" class="form-control" placeholder="Reference" @input="updateOrder" />
            </div>
          </div>
          <div class="col-sm-6 col-lg-4">
            <div class="form-group">
              <label class="control-label">Commodity</label>
              <selectize
                :options="commodityOptionsWithAll"
                name="commodity"
                v-model="commodity">
              </selectize>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-6 col-lg-4" v-if="$user.isAdminOrCoordinator">
            <div class="form-group">
              <label class="control-label">User</label>
              <selectize :options="initialUserOptions" v-model="user_id" name="user" :fetch-results="fetchUserResults"></selectize>
            </div>
          </div>
          <div class="col-sm-6 col-lg-4">
            <div class="form-group">
              <label class="control-label">Status</label>
              <selectize
                :options="statusOptions"
                name="status"
                v-model="status">
              </selectize>
            </div>
          </div>
          <div class="col-sm-6 col-lg-4">
            <div class="form-group">
              <label class="control-label">Date Range</label>
              <date-range v-model="dateRange" name="date_range"></date-range>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="ibox-content">
      <ul class="list-detail list-detail-job public-options">
        <job-item v-for="job in jobsDecorated" :key="job.id" :job="job"></job-item>
      </ul>

      <div class="text-center text-muted" v-show="haveNoJobs">
        <strong>There are no jobs.</strong>
      </div>
    </div>

    <lazy-loading v-show="haveJobs" :reached-bottom="getJobsForNextPage" :filters="filters" ref="lazyLoading">
      <strong slot="finished" class="text-muted">There are no more jobs.</strong>
    </lazy-loading>
  </div>
</template>

<script>
  import { mapActions, mapState, mapGetters } from 'vuex'
  import { debounce, isEqual, remove, cloneDeep } from 'lodash'
  import { LazyLoading } from 'components/misc'
  import { Selectize, DateRange } from 'components/forms'
  import { notify } from 'utils'
  import { Roles } from 'utils/user'
  import JobItem from './JobItem'

  export default {
    data () {
      return {
        initialUserOptions: [],
        commodity: 'all',
        status: 'all',
        keywords: '',
        order: '',
        dateRange: {
          from: null,
          to: null
        },
        user_id: null
      }
    },

    components: {
      LazyLoading,
      Selectize,
      DateRange,
      JobItem
    },

    computed: {
      /**
       * Map state/getters from the job and commodity modules.
       */
      ...mapState('job', ['jobs', 'pagination']),
      ...mapGetters('job', ['jobsDecorated']),
      ...mapGetters('commodity', ['commodityOptionsWithAll']),
      ...mapGetters('user', ['userOptionsWithAll']),

      filters () {
        return {
          commodity: this.commodity,
          status: this.status,
          keywords: this.keywords,
          order: this.order,
          from: this.dateRange.from,
          to: this.dateRange.to,
          user_id: this.user_id
        }
      },

      statusOptions () {
        let statuses = [
          { id: 'all', text: 'All' },
          { id: 'accepted', text: 'Accepted' },
          { id: 'scheduling', text: 'Scheduling' },
          { id: 'progress', text: 'In Progress' },
          { id: 'finalising', text: 'Finalising' },
          { id: 'invoiced', text: 'Invoiced' },
          { id: 'completed', text: 'Completed' },
          { id: 'pending', text: 'Pending' },
          { id: 'declined', text: 'Request Cancelled' },
          { id: 'cancelled', text: 'Cancelled' }
        ]

        if (this.$user.isAdminOrCoordinator) {
          remove(statuses, status => status.id === 'pending')
        }

        return statuses
      },

      /**
       * Determine if we have no jobs.
       *
       * @return boolean
       */
      haveNoJobs () {
        return this.jobs.length === 0
      },

      /**
       * Determine if we have jobs.
       *
       * @return boolean
       */
      haveJobs () {
        return this.jobs.length > 0
      }
    },

    watch: {
      /**
       * Watch filters for deep changes, refetch jobs if they change.
       *
       * @type Object
       */
      filters: {
        handler (newFilters, oldFilters) {
          if (isEqual(newFilters, oldFilters)) {
            return
          }

          // Reset the lazy loading component so that it will attempt to load
          // more jobs as we scroll again.
          const resetLazyLoading = () => (this.$refs.lazyLoading.$emit('reset'))

          this.getJobs(this.filters).then(resetLazyLoading).catch(this.failedToLoadJobs)
        },
        deep: true
      }
    },

    /**
     * On created get the jobs and commodities.
     *
     * @return void
     */
    created () {
      const failedToLoadCommodities = () => (notify.error('There was a problem loading the commodities.'))

      this.getJobs({sort: 'created_at'}).catch(this.failedToLoadJobs)

      if (this.$user.isAdminOrCoordinator) {
        const setInitialUserOptions = () => (this.initialUserOptions = cloneDeep(this.userOptionsWithAll))

        this.getUsers({ roles: [Roles.ADMIN, Roles.COORDINATOR, Roles.CARRIER, Roles.CLIENT], master: true }).then(setInitialUserOptions)

        if (this.$route.query.user) {
          this.user_id = this.$route.query.user
        }
      }
      this.getCommodities().catch(failedToLoadCommodities)
    },

    methods: {
      /**
       * Map actions from the job and commodity modules.
       */
      ...mapActions('job', ['getJobs', 'getJobsForNextPage']),
      ...mapActions('commodity', ['getCommodities']),
      ...mapActions('user', ['getUsers']),

      /**
       * Failed to load jobs.
       *
       * @return void
       */
      failedToLoadJobs () {
        notify.error('There was a problem loading the jobs.')
      },

      /**
       * When admin types in the user select box we need to fetch more users
       * from the api since the users are paginated.
       */
      fetchUserResults (name, callback) {
        return this.getUsers({ roles: [Roles.ADMIN, Roles.COORDINATOR, Roles.CARRIER, Roles.CLIENT], name, master: true }).then(response => {
          callback(this.userOptionsWithAll)
        })
      },

      /**
       * Debounce updating of keywords so we don't poll API immediately for new jobs.
       *
       * @param object event
       *
       * @return Function
       */
      updateKeywords: debounce(function (event) {
        this.keywords = event.target.value
      }, 800),

      /**
       * Debounce updating of order reference so we don't poll API immediately for new jobs.
       *
       * @param object event
       *
       * @return Function
       */
      updateOrder: debounce(function (event) {
        this.order = event.target.value
      }, 800)
    }
  }
</script>



// WEBPACK FOOTER //
// Jobs.vue?ea6a62da